import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  SEO_CONFIG,
  generateStructuredData,
  generateSocialTags,
} from "../utils/seo";

/**
 * Custom hook for managing SEO data and dynamic updates
 * @param {Object} seoData - SEO configuration object
 * @returns {Object} - SEO utilities and helpers
 */
export const useSEO = (seoData = {}) => {
  const location = useLocation();
  const previousPath = useRef();

  // Track page changes for analytics
  useEffect(() => {
    if (previousPath.current !== location.pathname) {
      // Notify Google Analytics of page change if gtag is available
      if (typeof gtag !== "undefined") {
        gtag("config", "G-FFE8HC4JJG", {
          page_path: location.pathname,
        });
      }

      // Notify other analytics services
      if (typeof fbq !== "undefined") {
        fbq("track", "PageView");
      }

      previousPath.current = location.pathname;
    }
  }, [location.pathname]);

  /**
   * Generate page-specific SEO data
   */
  const generatePageSEO = (overrides = {}) => {
    const defaults = {
      title: SEO_CONFIG.defaultTitle,
      description: SEO_CONFIG.defaultDescription,
      keywords: SEO_CONFIG.keywords,
      type: "website",
      image: `${SEO_CONFIG.siteUrl}/images/og-default.jpg`,
    };

    return {
      ...defaults,
      ...seoData,
      ...overrides,
      canonicalUrl:
        overrides.canonical || `${SEO_CONFIG.siteUrl}${location.pathname}`,
    };
  };

  /**
   * Generate breadcrumbs from current path
   */
  const generateBreadcrumbs = (customBreadcrumbs) => {
    if (customBreadcrumbs) return customBreadcrumbs;

    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs = [{ name: "Home", url: "/" }];

    let currentPath = "";
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Convert segment to readable name
      const name = segment
        .replace(/-/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());

      breadcrumbs.push({
        name,
        url: currentPath,
      });
    });

    return breadcrumbs;
  };

  /**
   * Generate structured data for current page
   */
  const generatePageStructuredData = (type, data) => {
    const baseStructuredData = [generateStructuredData("WebSite")];

    if (type && data) {
      baseStructuredData.push(generateStructuredData(type, data));
    }

    return baseStructuredData;
  };

  /**
   * Update page title dynamically
   */
  const updateTitle = (title) => {
    document.title = title
      ? `${title} | ${SEO_CONFIG.siteName}`
      : SEO_CONFIG.defaultTitle;
  };

  /**
   * Update meta description dynamically
   */
  const updateDescription = (description) => {
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", description);
    }
  };

  /**
   * Track user engagement for SEO
   */
  const trackEngagement = (eventName, properties = {}) => {
    // Track time on page
    if (eventName === "page_view") {
      const startTime = Date.now();

      return () => {
        const timeOnPage = Date.now() - startTime;

        // Send engagement metrics
        if (typeof gtag !== "undefined") {
          gtag("event", "page_engagement", {
            engagement_time_msec: timeOnPage,
            page_path: location.pathname,
          });
        }
      };
    }

    // Track specific events
    if (typeof gtag !== "undefined") {
      gtag("event", eventName, {
        page_path: location.pathname,
        ...properties,
      });
    }
  };

  /**
   * Check if current page should be indexed
   */
  const shouldIndex = () => {
    const noIndexPaths = ["/admin", "/private", "/404"];
    return !noIndexPaths.some((path) => location.pathname.startsWith(path));
  };

  /**
   * Generate social sharing data
   */
  const generateSocialData = (overrides = {}) => {
    const pageData = generatePageSEO(overrides);
    return generateSocialTags(pageData);
  };

  /**
   * Get current page SEO score (basic check)
   */
  const getSEOScore = () => {
    let score = 0;
    const checks = {
      title:
        document.title &&
        document.title.length > 30 &&
        document.title.length < 60,
      description:
        document.querySelector('meta[name="description"]')?.content?.length >
        120,
      h1: document.querySelector("h1"),
      canonical: document.querySelector('link[rel="canonical"]'),
      ogImage: document.querySelector('meta[property="og:image"]'),
      structuredData: document.querySelector(
        'script[type="application/ld+json"]',
      ),
    };

    Object.values(checks).forEach((check) => {
      if (check) score += 1;
    });

    return {
      score: Math.round((score / Object.keys(checks).length) * 100),
      checks,
    };
  };

  return {
    // Generators
    generatePageSEO,
    generateBreadcrumbs,
    generatePageStructuredData,
    generateSocialData,

    // Utilities
    updateTitle,
    updateDescription,
    trackEngagement,
    shouldIndex,
    getSEOScore,

    // Current page data
    currentPath: location.pathname,
    canonicalUrl: `${SEO_CONFIG.siteUrl}${location.pathname}`,
  };
};

/**
 * Hook for tracking page views and engagement
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view
    const startTime = Date.now();

    // Track Google Analytics page view
    if (typeof gtag !== "undefined") {
      gtag("config", "GA_MEASUREMENT_ID", {
        page_path: location.pathname,
      });
    }

    // Track scroll depth
    let maxScrollDepth = 0;
    const trackScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY /
          (document.documentElement.scrollHeight - window.innerHeight)) *
          100,
      );

      if (scrollPercent > maxScrollDepth) {
        maxScrollDepth = scrollPercent;

        // Track milestone scroll depths
        if ([25, 50, 75, 90].includes(scrollPercent)) {
          if (typeof gtag !== "undefined") {
            gtag("event", "scroll", {
              custom_parameter: scrollPercent,
            });
          }
        }
      }
    };

    window.addEventListener("scroll", trackScroll, { passive: true });

    // Cleanup function
    return () => {
      // Track engagement time
      const timeOnPage = Date.now() - startTime;
      if (typeof gtag !== "undefined") {
        gtag("event", "page_engagement", {
          engagement_time_msec: timeOnPage,
          page_path: location.pathname,
        });
      }
      window.removeEventListener("scroll", trackScroll);
    };
  }, [location.pathname]);
};

/**
 * Hook for managing dynamic meta tags
 */
export const useDynamicMeta = (initialMeta = {}) => {
  const location = useLocation();

  const updateMeta = (metaData) => {
    Object.entries(metaData).forEach(([key, value]) => {
      let selector;

      if (key.startsWith("og:")) {
        selector = `meta[property="${key}"]`;
      } else {
        selector = `meta[name="${key}"]`;
      }

      let metaElement = document.querySelector(selector);

      if (!metaElement) {
        metaElement = document.createElement("meta");
        if (key.startsWith("og:")) {
          metaElement.setAttribute("property", key);
        } else {
          metaElement.setAttribute("name", key);
        }
        document.head.appendChild(metaElement);
      }

      metaElement.setAttribute("content", value);
    });
  };

  useEffect(() => {
    if (Object.keys(initialMeta).length > 0) {
      updateMeta(initialMeta);
    }
  }, [location.pathname]);

  return { updateMeta };
};

export default useSEO;
